import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
    Row, Col, Card, CardBody, Button, Table,
    Nav, NavItem, NavLink, TabContent, TabPane, Badge, Modal, ModalBody
} from "reactstrap";
import classnames from "classnames";

const DispatchView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("expenses");
    const [previewImage, setPreviewImage] = useState(null);
    const [dispatchInfo, setDispatchInfo] = useState(null);
    const [activities, setActivities] = useState({ expenses: [], salary: [], documents: [] });

    // Add these console logs to debug
    const loadFullData = useCallback(async () => {
        dispatch(loadingTrue());
        try {
            // 1. Fetch Dispatch Basic Info
            const infoRes = await Handler({
                method: "get",
                url: `dispatch/view.php?id=${id}`
            });

            // Handler.js 'success: true' nu tharudhu
            if (infoRes?.success) {
                setDispatchInfo(infoRes.data);
            } else {
                setDispatchInfo({});
            }

            // 2. Fetch Financial Activities (Expenses, Salary, etc.)
            const activityRes = await Handler({
                method: "get",
                url: `dispatch/view_financials.php?dispatch_id=${id}&mode=all`
            });

            console.log("Full Activity Response from Handler:", activityRes);

            if (activityRes?.success) {
                // Handler.js unga root objects-ah 'data' kulla pottu tharudhu
                // So: activityRes.data.expenses.data
                const financialData = activityRes.data;

                setActivities({
                    expenses: financialData?.expenses?.data || [],
                    salary: financialData?.salary?.data || [],
                    documents: financialData?.documents?.data || []
                });
            }
        } catch (err) {
            console.error("API Error:", err);
            setDispatchInfo({});
        }
        dispatch(loadingFalse());
    }, [id, dispatch]);

    useEffect(() => { loadFullData(); }, [loadFullData]);

    // Indha condition irundha dhan data varala na kooda page crash aagadhu
    if (!dispatchInfo) return null;

    const assignedVehicles = dispatchInfo.assigned_vehicles || [];

    return (
        <div className="execution-container p-3 bg-light min-vh-100">
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-4 shadow-sm border-start border-primary border-5">
                <div>
                    <h4 className="fw-bold mb-0 text-dark">
                        <i className="bi bi-truck me-2 text-primary"></i>
                        Execution: {dispatchInfo.dispatch?.dispatch_number}
                    </h4>
                    <span className="small text-muted text-uppercase">Date: {dispatchInfo.dispatch?.dispatch_date}</span>
                </div>
                <Button color="secondary" outline size="sm" className="rounded-pill px-4" onClick={() => navigate(-1)}>Back</Button>
            </div>

            <Row className="g-4">
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 mb-4">
                        <div className="p-3 bg-primary text-white fw-bold small text-uppercase rounded-top-4">Logistics Summary</div>
                        <CardBody className="p-4">
                            <div className="mb-4">
                                <label className="extra-small text-muted fw-bold text-uppercase">Customer</label>
                                <h5 className="fw-bold text-dark">{dispatchInfo.customer?.company_name}</h5>
                            </div>
                            <div className="p-3 bg-light rounded-4 border">
                                <div className="d-flex align-items-center justify-content-between text-center">
                                    <div>
                                        <div className="fw-bold small">{dispatchInfo.dispatch?.discharge_point}</div>
                                        <div className="extra-small text-muted">Discharge</div>
                                    </div>
                                    <i className="bi bi-arrow-right text-primary h4 mb-0"></i>
                                    <div>
                                        <div className="fw-bold small">{dispatchInfo.dispatch?.end_point}</div>
                                        <div className="extra-small text-muted">Destination</div>
                                    </div>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    <Card className="border-0 shadow-sm rounded-4">
                        <div className="p-3 bg-dark text-white fw-bold small text-uppercase rounded-top-4">Assigned Drivers</div>
                        <CardBody className="p-0">
                            {assignedVehicles?.map((v, i) => (
                                <div key={i} className="p-3 border-bottom d-flex align-items-center justify-content-between">
                                    <div>
                                        <div className="fw-bold small">{v.driver_name}</div>
                                        <div className="extra-small text-muted">{v.vehicle_number}</div>
                                    </div>
                                    <a href={`tel:${v.driver_phone}`} className="btn btn-sm btn-light rounded-circle text-primary"><i className="bi bi-telephone"></i></a>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={8}>
                    <Nav tabs className="border-0 bg-white p-1 rounded-pill shadow-sm mb-4 border" style={{ width: 'fit-content' }}>
                        {['expenses', 'salary', 'documents']?.map(t => (
                            <NavItem key={t}>
                                <NavLink className={classnames("border-0 rounded-pill px-4 fw-bold cursor-pointer", { active: activeTab === t })} onClick={() => setActiveTab(t)} style={{ cursor: 'pointer' }}>
                                    {t.toUpperCase()}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>

                    <TabContent activeTab={activeTab}>
                        <TabPane tabId="expenses">
                            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="bg-light small">
                                        <tr><th className="ps-3">Type</th><th>Date</th><th className="text-end pe-4">Amount</th><th>Remarks</th></tr>
                                    </thead>
                                    <tbody>
                                        {activities.expenses.map((ex, i) => (
                                            <tr key={i}>
                                                <td className="ps-3"><Badge color="soft-info" className="text-info bg-info bg-opacity-10 px-2 py-1 text-uppercase">{ex.expense_type}</Badge></td>
                                                <td className="text-muted small">{dayjs(ex.expense_date).format("DD-MM-YYYY")}</td>
                                                <td className="fw-bold text-danger text-end pe-4">-${parseFloat(ex.amount).toFixed(2)}</td>
                                                <td className="small text-muted">{ex.remarks}</td>
                                            </tr>
                                        ))}
                                        {activities.expenses.length === 0 && <tr><td colSpan="4" className="text-center py-5">No records found</td></tr>}
                                    </tbody>
                                </Table>
                            </Card>
                        </TabPane>

                        <TabPane tabId="salary">
                            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="bg-light small">
                                        <tr><th className="ps-3">Driver Name</th><th>Date</th><th className="text-end pe-4">Amount</th><th>Remarks</th></tr>
                                    </thead>
                                    <tbody>
                                        {activities.salary.map((s, i) => (
                                            <tr key={i}>
                                                <td className="ps-3 fw-bold text-dark">{s.driver_name}</td>
                                                <td className="text-muted small">{dayjs(s.salary_date).format("DD-MM-YYYY")}</td>
                                                <td className="text-success fw-bold text-end pe-4">${parseFloat(s.salary_amount).toFixed(2)}</td>
                                                <td className="small text-muted">{s.remarks}</td>
                                            </tr>
                                        ))}
                                        {activities.salary.length === 0 && <tr><td colSpan="4" className="text-center py-5">No payout history</td></tr>}
                                    </tbody>
                                </Table>
                            </Card>
                        </TabPane>

                        <TabPane tabId="documents">
                            <Card className="border-0 shadow-sm rounded-4">
                                <CardBody>
                                    <Row className="g-3">
                                        {activities.documents.map((doc, i) => (
                                            <Col md={3} key={i}>
                                                <div className="rounded-4 overflow-hidden border shadow-sm bg-white h-100 position-relative">
                                                    <img
                                                        // src={`http://localhost/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`}
                                                        src={`https://sfs.software.dizoratbd.com/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`}
                                                        className="img-fluid cursor-pointer"
                                                        style={{ height: '180px', width: '100%', objectFit: 'cover' }}
                                                        // onClick={() => setPreviewImage(`http://localhost/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`)}
                                                        onClick={() => setPreviewImage(`https://sfs.software.dizoratbd.com/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`)}
                                                        alt="pod"
                                                    />
                                                    <div className="p-2 extra-small text-center fw-bold text-truncate bg-light border-top">{doc.file_name}</div>
                                                </div>
                                            </Col>
                                        ))}
                                        {activities.documents.length === 0 && <Col md={12} className="text-center py-5 text-muted">No documents uploaded</Col>}
                                    </Row>
                                </CardBody>
                            </Card>
                        </TabPane>
                    </TabContent>
                </Col>
            </Row>

            <Modal isOpen={!!previewImage} toggle={() => setPreviewImage(null)} size="lg" centered>
                <ModalBody className="p-1 bg-dark rounded-4 text-center">
                    <img src={previewImage} className="img-fluid rounded-3" alt="full" style={{ maxHeight: '85vh' }} />
                    <div className="p-2"><Button color="light" size="sm" className="rounded-pill" onClick={() => setPreviewImage(null)}>Close</Button></div>
                </ModalBody>
            </Modal>
        </div>
    );
};

export default DispatchView;